/**
 * Source of truth for the Admin · Settings page: the **core** config sections and
 * their inline help copy (distilled from `.env.example` comments). Plugins add
 * their own sections via `manifest.settings` (see `@d12labs/plugins`'s
 * `PluginSettingsSection`) — `composeSettingsSections` interleaves them.
 *
 * This module is pure data (no React, no plugin imports) so it is safe to import
 * from both the client page and server code (the settings registry derives the
 * movable-key set from it). The env-vs-editable *state* is NOT encoded here —
 * it's computed live from the runtime environment (see `src/api/settings-routes`).
 */

export type SettingRowDef = {
	/** Env var name (e.g. "OPENAI_BASE_URL"). */
	key: string;
	/** Human-readable row title. */
	label: string;
	/** Inline help paragraph — the new operator context. */
	help: string;
	/** Masked + encrypted at rest; plaintext never reaches the client. */
	secret?: boolean;
	/** Read once at startup — an edit only applies after a server restart (OAuth). */
	restartRequired?: boolean;
	/** Input placeholder when unset (a default or example). */
	placeholder?: string;
	/**
	 * Closed list of allowed values — rendered as a select; the API rejects
	 * off-list writes. `value` is stored / read from env. Not for secrets.
	 */
	options?: { value: string; label: string }[];
	/**
	 * Boot floor: deploy-time only, never runtime-editable (even when not env-set).
	 * Shown read-only for visibility. `DATABASE_URL`/`BETTER_AUTH_SECRET`/`ADMIN_EMAILS`/`PORT`.
	 */
	floor?: boolean;
	/**
	 * The app cannot boot without this key (a strict subset of `floor` — PORT and
	 * ADMIN_EMAILS have workable defaults, these don't). `requireFloor()` in
	 * `@/lib/env` derives its fail-fast list from this flag, so "what's required
	 * at boot" has exactly one home.
	 */
	bootRequired?: boolean;
};

export type SettingsSection = {
	/** Stable nav anchor id. */
	id: string;
	/** Section heading. */
	title: string;
	/** Lucide icon name for the section chip (page falls back if absent). */
	icon?: string;
	/** One-line section description. */
	blurb?: string;
	/** Optional footnote rendered under the section card. */
	footnote?: string;
	rows: SettingRowDef[];
};

/** Core sections shown before any plugin-contributed sections. */
export const CORE_SECTIONS_LEAD: SettingsSection[] = [
	{
		id: "core",
		title: "Core",
		icon: "Cog",
		blurb:
			"The boot-required floor. The app fails fast without DATABASE_URL and BETTER_AUTH_SECRET. Shown for visibility — change these in your host config and redeploy, never at runtime.",
		footnote:
			"These are deploy-time only. There is no Save here by design — editing them live would risk locking everyone out.",
		rows: [
			{
				key: "DATABASE_URL",
				label: "Database URL",
				secret: true,
				floor: true,
				bootRequired: true,
				help: "Postgres connection string. Required — the server refuses to boot without it.",
			},
			{
				key: "BETTER_AUTH_SECRET",
				label: "Auth secret",
				secret: true,
				floor: true,
				bootRequired: true,
				help: "Better Auth signing secret (openssl rand -base64 32). Also keys at-rest encryption for connected-app credentials — rotating it invalidates sessions and stored integrations.",
			},
			{
				key: "PORT",
				label: "Port",
				floor: true,
				help: "Port the server listens on. PORT, BETTER_AUTH_URL and APP_URL must all agree, or auth callbacks break.",
			},
		],
	},
	// @scaffold:ai start
	{
		id: "ai",
		title: "AI",
		icon: "Sparkles",
		blurb: "Claude + OpenAI-compatible providers, spend caps and alerts.",
		rows: [
			{
				key: "ANTHROPIC_API_KEY",
				label: "Anthropic API key",
				secret: true,
				help: "Enables Claude calls across every plugin. Leave blank to disable AI features entirely.",
			},
			{
				key: "OPENAI_API_KEY",
				label: "OpenAI / compatible API key",
				secret: true,
				help: "Serves two uses: Speech Coach audio transcription (Whisper) and the openai-compatible AI provider. Blank disables both. May be blank for a local self-hosted endpoint.",
			},
			{
				key: "OPENAI_BASE_URL",
				label: "OpenAI-compatible base URL",
				placeholder: "https://api.openai.com/v1",
				help: "Point at a local /v1 endpoint to reach local LLMs with no code change — Ollama (:11434/v1), LM Studio (:1234/v1), vLLM, or a gateway. Blank = OpenAI. Local/unknown models are uncosted ($0) but still logged.",
			},
			{
				key: "AI_DEFAULT_MODEL",
				label: "Default model",
				placeholder: "claude-sonnet-4-6",
				help: "Model used when a request doesn't name one.",
			},
			{
				key: "AI_DAILY_CAP_USD",
				label: "Daily spend cap (USD)",
				placeholder: "50",
				help: "Hard cap on AI spend per UTC day. Requests are refused once crossed. Blank = no cap.",
			},
			{
				key: "AI_ALERT_WEBHOOK_URL",
				label: "Spend-alert webhook URL",
				secret: true,
				placeholder: "https://hooks.slack.com/…",
				help: 'POST’d at 50 / 80 / 100% cap-threshold crossings with Slack-compatible JSON: {"text": "AI spend at 80%: $40.12 / $50.00"}.',
			},
		],
	},
	// @scaffold:ai end
	// @scaffold:email start
	{
		id: "email",
		title: "Email",
		icon: "Mail",
		blurb: "Transactional delivery via Resend.",
		rows: [
			{
				key: "RESEND_API_KEY",
				label: "Resend API key",
				secret: true,
				placeholder: "re_…",
				help: "Sends transactional email through Resend. Leave blank in dev to log emails to the console instead of sending.",
			},
			{
				key: "EMAIL_FROM",
				label: "From address",
				placeholder: "You <you@your-domain.com>",
				help: "The verified sender. Resend’s default onboarding@resend.dev only delivers to your own account email — verify a custom domain for real delivery.",
			},
			{
				key: "APP_NAME",
				label: "App name",
				placeholder: "d12-starter",
				help: "Shown in email subjects and the workspace header.",
			},
		],
	},
	// @scaffold:email end
	// @scaffold:storage start
	{
		id: "storage",
		title: "Storage",
		icon: "Database",
		blurb: "Where uploaded files live — S3-compatible object storage or the local filesystem.",
		rows: [
			{
				key: "STORAGE_DRIVER",
				label: "Driver",
				placeholder: "s3  ·  local",
				help: "s3 (default) or local. Set local to back uploads with the filesystem — then docker compose up postgres is enough, no MinIO needed. Local is a single-node dev driver, not for multi-machine prod.",
			},
			{
				key: "S3_BUCKET",
				label: "S3 bucket",
				placeholder: "d12-starter",
				help: "Bucket uploads are written to. Blank disables uploads.",
			},
			{
				key: "S3_REGION",
				label: "S3 region",
				placeholder: "us-east-1",
				help: "AWS region of the bucket. Defaults to us-east-1.",
			},
			{
				key: "S3_ENDPOINT",
				label: "S3 endpoint",
				placeholder: "http://localhost:9100",
				help: "Custom endpoint for MinIO (dev) or Cloudflare R2. Leave blank for AWS S3.",
			},
			{
				key: "S3_ACCESS_KEY_ID",
				label: "S3 access key id",
				secret: true,
				help: "Access key for the S3-compatible store.",
			},
			{
				key: "S3_SECRET_ACCESS_KEY",
				label: "S3 secret access key",
				secret: true,
				help: "Secret key paired with the access key id above.",
			},
		],
	},
	// @scaffold:storage end
	// @scaffold:oauth start
	{
		id: "oauth",
		title: "OAuth",
		icon: "KeyRound",
		blurb:
			'Social sign-in providers. Each is opt-in — set BOTH the client id and secret, or the "Continue with X" button stays hidden on login.',
		footnote:
			// biome-ignore lint/suspicious/noTemplateCurlyInString: literal operator copy — shown verbatim, not interpolated
			"Redirect URI for all three: ${BETTER_AUTH_URL}/api/auth/callback/<provider>. Changes to OAuth credentials take effect after a server restart.",
		rows: [
			{
				key: "GOOGLE_CLIENT_ID",
				label: "Google client id",
				restartRequired: true,
				placeholder: "not set",
				help: "From console.cloud.google.com → APIs & Services → Credentials.",
			},
			{
				key: "GOOGLE_CLIENT_SECRET",
				label: "Google client secret",
				secret: true,
				restartRequired: true,
				help: "The secret paired with the Google client id above.",
			},
			{
				key: "GITHUB_CLIENT_ID",
				label: "GitHub client id",
				restartRequired: true,
				placeholder: "not set",
				help: "From github.com/settings/developers → OAuth Apps.",
			},
			{
				key: "GITHUB_CLIENT_SECRET",
				label: "GitHub client secret",
				secret: true,
				restartRequired: true,
				help: "The secret paired with the GitHub client id above.",
			},
			{
				key: "MICROSOFT_CLIENT_ID",
				label: "Microsoft client id",
				restartRequired: true,
				placeholder: "not set",
				help: "From portal.azure.com → App registrations.",
			},
			{
				key: "MICROSOFT_CLIENT_SECRET",
				label: "Microsoft client secret",
				secret: true,
				restartRequired: true,
				help: "The secret paired with the Microsoft client id above.",
			},
		],
	},
	// @scaffold:oauth end
];

/** Core sections shown after any plugin-contributed sections. */
export const CORE_SECTIONS_TAIL: SettingsSection[] = [
	{
		id: "access",
		title: "Access & logging",
		icon: "ShieldCheck",
		blurb:
			"Who can see admin surfaces, the app timezone for local-day scheduling, and how loud the server logs.",
		rows: [
			{
				key: "ADMIN_EMAILS",
				label: "Admin emails",
				floor: true,
				help: "Comma-separated allowlist of users who can see admin surfaces (this page, AI usage, users). Admin is config, not a DB role. Blank = no admins, and the admin nav simply doesn’t appear.",
			},
			{
				key: "APP_TIMEZONE",
				label: "App timezone",
				placeholder: "America/Chicago",
				help: 'IANA timezone used for local-day scheduling — streaks and reminder nudges count "calendar days" in this zone, not UTC.',
			},
			{
				key: "LOG_LEVEL",
				label: "Log level",
				placeholder: "default (debug in dev, info in production)",
				help: "Structured Pino output verbosity. Defaults: debug in dev, info in production. Use warn/error to quiet down, trace to firehose.",
				options: [
					{ value: "trace", label: "trace" },
					{ value: "debug", label: "debug" },
					{ value: "info", label: "info" },
					{ value: "warn", label: "warn" },
					{ value: "error", label: "error" },
					{ value: "fatal", label: "fatal" },
				],
			},
		],
	},
	{
		id: "jobs",
		title: "Background jobs",
		icon: "ListChecks",
		blurb: "How the background worker runs queued work (AI scoring, imports, ingest).",
		rows: [
			{
				key: "JOBS_CONCURRENCY",
				label: "Jobs at once",
				restartRequired: true,
				placeholder: "1",
				help: "How many background jobs the worker runs at the same time (1–16). Default 1. Most jobs wait on an AI or third-party API, so 2–4 clears a backlog faster; mind the AI provider's concurrent-request limit. Ledger jobs always run one at a time. Applies after a server restart.",
			},
		],
	},
];

/** All core sections in display order (no plugin sections). */
export const CORE_SECTIONS: SettingsSection[] = [...CORE_SECTIONS_LEAD, ...CORE_SECTIONS_TAIL];

/** Every core row, flattened (used by the settings registry to derive movable keys). */
export const CORE_SETTING_ROWS: SettingRowDef[] = CORE_SECTIONS.flatMap((s) => s.rows);

/**
 * Keys the app cannot boot without (`bootRequired: true` rows). The single source
 * for `requireFloor()`'s fail-fast list — add the flag here, never a parallel list.
 */
export function bootRequiredKeys(): string[] {
	return CORE_SETTING_ROWS.filter((r) => r.bootRequired).map((r) => r.key);
}

/**
 * Compose the full settings schema: core lead sections → plugin sections → core
 * tail sections. Plugin sections come from the plugin registry (`manifest.settings`).
 */
export function composeSettingsSections(pluginSections: SettingsSection[]): SettingsSection[] {
	return [...CORE_SECTIONS_LEAD, ...pluginSections, ...CORE_SECTIONS_TAIL];
}
